import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronLeftIcon, ChevronRightIcon, PlayIcon } from '@heroicons/react/24/outline'
import EnquiryModal from '../../../components/Enquiry/EnquiryModal'

const HeroSection = ({ banners = [] }) => {
    const [currentSlide, setCurrentSlide] = useState(0)
    const [isEnquiryOpen, setIsEnquiryOpen] = useState(false)

    useEffect(() => {
        if (banners.length === 0) return

        const timer = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % banners.length)
        }, 5000)

        return () => clearInterval(timer)
    }, [banners.length])

    const nextSlide = () => {
        setCurrentSlide((prev) => (prev + 1) % banners.length)
    }

    const prevSlide = () => {
        setCurrentSlide((prev) => (prev - 1 + banners.length) % banners.length)
    }

    const goToSlide = (index) => {
        setCurrentSlide(index)
    }

    if (banners.length === 0) {
        return (
            <section className="relative h-screen bg-gradient-to-br from-primary-600 via-secondary-600 to-primary-800 flex items-center justify-center">
                <div className="absolute inset-0 bg-black/20"></div>
                <div className="relative z-10 text-center text-white px-4 sm:px-6 lg:px-8">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
                            Transform Your Career with
                            <span className="block text-accent-400">Online Education</span>
                        </h1>
                        <p className="text-xl sm:text-2xl mb-8 max-w-3xl mx-auto opacity-90">
                            Get UGC Recognized Degrees from Top Universities. Join thousands of successful professionals who have advanced their careers with our flexible online programs.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button
                                onClick={() => setIsEnquiryOpen(true)}
                                className="btn-primary btn-lg hover-glow"
                            >
                                Get Free Consultation
                            </button>
                            <Link
                                to="/courses"
                                className="btn-outline btn-lg text-white border-white hover:bg-white hover:text-primary-600"
                            >
                                Explore Courses
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>
        )
    }

    return (
        <>
            <section className="relative h-auto min-h-[500px] sm:h-[85vh] sm:min-h-[600px] overflow-hidden">
                <AnimatePresence mode="wait">
                    {banners.map((banner, index) => (
                        <motion.div
                            key={banner.id}
                            initial={{ opacity: 0, x: index === currentSlide ? 0 : 100 }}
                            animate={{
                                opacity: index === currentSlide ? 1 : 0,
                                x: index === currentSlide ? 0 : -100
                            }}
                            exit={{ opacity: 0, x: -100 }}
                            transition={{ duration: 0.5 }}
                            className={`absolute inset-0 ${index === currentSlide ? 'block' : 'hidden'
                                }`}
                        >
                            {/* Background Image */}
                            <div
                                className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                                style={{
                                    backgroundImage: `url(${banner.image_url})`,
                                }}
                            >
                                <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent"></div>
                            </div>

                            <div className="relative z-10 h-full flex items-center py-20 sm:py-0">
                                <div className="container mx-auto px-4 sm:px-6 lg:px-8 w-full">
                                    <div className="max-w-2xl">
                                        <motion.div
                                            initial={{ opacity: 0, y: 30 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.8, delay: 0.2 }}
                                        >
                                            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6 leading-tight">
                                                {banner.title}
                                            </h1>
                                        </motion.div>

                                        {banner.subtitle && (
                                            <motion.p
                                                initial={{ opacity: 0, y: 30 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ duration: 0.8, delay: 0.4 }}
                                                className="text-lg sm:text-2xl text-gray-200 mb-4 sm:mb-6 text-white"
                                            >
                                                {banner.subtitle}
                                            </motion.p>
                                        )}

                                        {banner.description && (
                                            <motion.p
                                                initial={{ opacity: 0, y: 30 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ duration: 0.8, delay: 0.6 }}
                                                className="text-lg text-gray-300 mb-8 max-w-xl text-white"
                                            >
                                                {banner.description}
                                            </motion.p>
                                        )}

                                        <motion.div
                                            initial={{ opacity: 0, y: 30 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.8, delay: 0.8 }}
                                            className="flex flex-col sm:flex-row gap-4"
                                        >
                                            {banner.cta_type === 'enquiry' ? (
                                                <button
                                                    onClick={() => setIsEnquiryOpen(true)}
                                                    className="btn-primary btn-lg hover-glow"
                                                >
                                                    {banner.cta_text}
                                                </button>
                                            ) : banner.cta_type === 'modal' ? (
                                                <button
                                                    onClick={() => setIsEnquiryOpen(true)}
                                                    className="btn-primary btn-lg hover-glow"
                                                >
                                                    {banner.cta_text}
                                                </button>
                                            ) : (
                                                <Link
                                                    to={banner.cta_link}
                                                    className="btn-primary btn-lg hover-glow"
                                                >
                                                    {banner.cta_text}
                                                </Link>
                                            )}
                                        </motion.div>
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </AnimatePresence>

                {/* Navigation Arrows */}
                {banners.length > 1 && (
                    <>
                        <button
                            onClick={prevSlide}
                            className="absolute left-4 top-1/2 -translate-y-1/2 z-20 p-3 bg-white/20 backdrop-blur-sm rounded-full text-white hover:bg-white/30 transition-all duration-200"
                        >
                            <ChevronLeftIcon className="w-6 h-6" />
                        </button>
                        <button
                            onClick={nextSlide}
                            className="absolute right-4 top-1/2 -translate-y-1/2 z-20 p-3 bg-white/20 backdrop-blur-sm rounded-full text-white hover:bg-white/30 transition-all duration-200"
                        >
                            <ChevronRightIcon className="w-6 h-6" />
                        </button>
                    </>
                )}

                {/* Dots Indicator */}
                {banners.length > 1 && (
                    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex space-x-2">
                        {banners.map((_, index) => (
                            <button
                                key={index}
                                onClick={() => goToSlide(index)}
                                className={`w-3 h-3 rounded-full transition-all duration-200 ${index === currentSlide
                                    ? 'bg-white'
                                    : 'bg-white/50 hover:bg-white/70'
                                    }`}
                            />
                        ))}
                    </div>
                )}

                {/* Scroll Indicator */}
                <div className="absolute bottom-8 right-8 z-20 animate-bounce">
                    <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
                        <div className="w-1 h-3 bg-white/50 rounded-full mt-2 animate-pulse"></div>
                    </div>
                </div>
            </section>

            {/* Enquiry Modal */}
            <EnquiryModal
                isOpen={isEnquiryOpen}
                onClose={() => setIsEnquiryOpen(false)}
            />
        </>
    )
}

export default HeroSection
